import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { Link, useNavigate } from 'react-router-dom'

import 'react-toastify/dist/ReactToastify.css'
import { Box, Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { FormProvider } from '../../common/hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { apiConstants } from '../../common/constants'
import axios from 'axios'
import {
  DividerText,
  ForgotPasswordButton,
  HeaderContainer,
  IconContainer,
  StyledDivider,
  StyledIcon,
  StyledLoadingButton,
  StyledTextField,
  SubTitle,
  WelcomeTitle,
} from './StyledElements'
import { Login, VerifiedUser } from '@mui/icons-material'

export default function ResetPassword() {
  const [isLoading, setIsLoading] = React.useState(false)
  const { rpInfo } = useSelector((state) => state.resetPasswordSlice)
  const { fpInfo, userEmail } = useSelector((state) => state.forgotPasswordSlice)
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = React.useState(0)
  const { enqueueSnackbar } = useSnackbar()
  // redirect authenticated user to profile screen
  useEffect(() => {
    if (Object.keys(fpInfo).length !== 0) {
      enqueueSnackbar('Please check your email for OTP.', { variant: 'success' })
    }
    if (Object.keys(rpInfo).length !== 0) {
      navigate('/')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, rpInfo])

  const onSubmit = async (reqData) => {
    reqData.username = userEmail
    setIsLoading(true)
    try {
      // configure header's Content-Type as JSON
      const config = {
        headers: {
          'Content-Type': 'application/json',
        },
      }
      const url = `${apiConstants.BASE_LOCAL}${apiConstants.RESET_PASSWORD}`
      const { data } = await axios.post(url, reqData, config)
      enqueueSnackbar(data.message || 'Password changed successfully', { variant: 'success' })
      navigate('/')
      setIsLoading(false)
      return data
    } catch (e) {
      console.log(e)
      enqueueSnackbar(e.response?.data?.message || 'Something went wrong', { variant: 'error' })
      setIsLoading(false)
    }
  }

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        // username: Yup.string().required("Please enter username"),
        current_password: Yup.string().required('Please enter OTP recieved on email'),
        new_password: Yup.string()
          .required('Please enter a valid password')
          .min(8, 'Password must be at least 8 characters')
          .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
          .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
          .matches(/[0-9]/, 'Password must contain at least one number'),
        confirm_password: Yup.string()
          .required('Please confirm your password')
          .oneOf([Yup.ref('new_password'), null], 'Passwords must match'), // Custom validation
      })
    ),
    defaultValues: {
      // username: '',
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
  })

  const { handleSubmit } = methods

  return (
    <Box>
      <HeaderContainer>
        <WelcomeTitle variant='h4'>Reset Password</WelcomeTitle>
        <SubTitle variant='body1' color='text.secondary'>
          Please provide your details below to reset your password
        </SubTitle>
      </HeaderContainer>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant='subtitle1'>{userEmail}</Typography>
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              name='current_password'
              size='large'
              label='Enter OTP ( Please check email for OTP )'
              type='text'
              placeholder='Please enter OTP recieved on email'
              mandatory
              autoFocus
              trim
              InputProps={{
                startAdornment: (
                  <IconContainer>
                    <StyledIcon>
                      <VerifiedUser />
                    </StyledIcon>
                  </IconContainer>
                ),
              }}
              autoComplete='new-password'
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              name='new_password'
              size='large'
              label='New Password'
              mandatory
              placeholder='Please enter your new password'
              type={showPassword ? 'text' : 'password'}
              trim
              autoComplete='new-password'
            />
          </Grid>
          <Grid item xs={12}>
            <StyledTextField
              name='confirm_password'
              size='large'
              label='Confirm Password'
              mandatory
              placeholder='Please re-enter your new password'
              type={showPassword ? 'text' : 'password'}
              trim
              autoComplete='new-password'
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              sx={{
                '& .MuiCheckbox-root': {
                  color: '#000 !important',
                },
                '& .MuiFormControlLabel-label': {
                  color: '#000 !important',
                },
              }}
              onChange={() => setShowPassword((v) => !v)}
              checked={showPassword}
              control={<Checkbox />}
              label='Show passwords'
            />
          </Grid>
          <Grid item xs={12}>
            <Stack spacing={2}>
              <StyledLoadingButton
                loading={isLoading}
                type='submit'
                size='large'
                color='warning'
                variant='contained'
                fullWidth
                startIcon={<Login />}
              >
                Submit
              </StyledLoadingButton>
              <StyledDivider>
                <DividerText variant='body2' color='text.secondary'>
                  OR
                </DividerText>
              </StyledDivider>
              <ForgotPasswordButton variant='text' component={Link} to='/login'>
                Back to Login
              </ForgotPasswordButton>
            </Stack>
          </Grid>
        </Grid>
      </FormProvider>
    </Box>
  )
}
